package org.teddapp.project.ui.objectives

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Category
import com.tedd.db.Milestone
import com.tedd.db.Objective
import com.tedd.db.Skill
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddTokenProgress
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddDurationChips

/**
 * Slide-in editor 480dp côté droit. CRUD objectifs + jalons inline.
 *
 * Validation : titre requis, date au format YYYY-MM-DD (ou vide).
 */
data class ObjectiveDraft(
    val title: String,
    val description: String,
    val motivation: String,
    val deadline: String,
    val year: Long?,
    val status: String,
    val progress: Int,
    val categoryId: String?,
    /** Compétence que cet objectif fait progresser, ou null. */
    val skillId: String?,
    val milestones: List<MilestoneDraft>,
    /** Décomposition proposée à la création : des micro-tâches de 5 à 10 minutes. Ignorée en modification. */
    val microTasks: List<MicroTaskDraft> = emptyList(),
)

/** Une micro-tâche de la décomposition initiale. */
data class MicroTaskDraft(val title: String, val minutes: Int?)

/** Les trois premiers pas proposés par défaut (Duneier) ; l'utilisateur garde, modifie, ajoute. */
internal val DEFAULT_MICRO_TASKS: List<MicroTaskDraft> = listOf(
    MicroTaskDraft("Relire la consigne et noter l'obstacle principal", 5),
    MicroTaskDraft("Isoler la plus petite partie à faire", 10),
    MicroTaskDraft("La faire, puis écrire une phrase d'auto-explication", 10),
)

data class MilestoneDraft(
    /** null = nouveau jalon à créer. Sinon : id d'un jalon existant à mettre à jour. */
    val id: String?,
    val title: String,
    val deadline: String,
    val progress: Int = 0,
)

@Composable
fun ObjectiveEditor(
    initial: Objective?,
    initialMilestones: List<Milestone>,
    categories: List<Category>,
    skills: List<Skill> = emptyList(),
    onClose: () -> Unit,
    onSave: (ObjectiveDraft) -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier.width(480.dp).fillMaxHeight(),
) {
    val isNew = initial == null
    var title by remember(initial) { mutableStateOf(initial?.title ?: "") }
    var description by remember(initial) { mutableStateOf(initial?.description ?: "") }
    var motivation by remember(initial) { mutableStateOf(initial?.motivation ?: "") }
    var deadline by remember(initial) { mutableStateOf(initial?.deadline ?: "") }
    var status by remember(initial) { mutableStateOf(initial?.status ?: "active") }
    var progress by remember(initial) { mutableStateOf((initial?.progress?.toInt() ?: 0)) }
    var categoryId by remember(initial) {
        mutableStateOf(initial?.category_id ?: categories.firstOrNull()?.id)
    }
    var skillId by remember(initial) { mutableStateOf(initial?.skill_id) }
    var microTasks by remember(initial?.id ?: "new") { mutableStateOf(if (initial == null) DEFAULT_MICRO_TASKS else emptyList()) }
    var milestoneDrafts by remember(initial?.id ?: "new", initialMilestones) {
        mutableStateOf(
            initialMilestones.map {
                MilestoneDraft(id = it.id, title = it.title, deadline = it.deadline ?: "", progress = it.progress.toInt())
            }
        )
    }

    val titleError: String? = if (title.isBlank()) null else null  // affiché uniquement après touch (V1 : pas de touch tracker)
    val deadlineError: String? = validateDate(deadline)
    val anyMsDeadlineError = milestoneDrafts.any { validateDate(it.deadline) != null }
    val anyMsTitleError = milestoneDrafts.any { it.title.isBlank() }
    // Cross-field : un jalon ne peut pas avoir de deadline POSTÉRIEURE à celle de l'objectif.
    // Comparaison lexicographique sûre sur des ISO YYYY-MM-DD valides.
    val objDeadlineTrimmed = deadline.trim()
    val anyMsDeadlineAfterObjective = objDeadlineTrimmed.isNotBlank() &&
        validateDate(objDeadlineTrimmed) == null &&
        milestoneDrafts.any { d ->
            val md = d.deadline.trim()
            md.isNotBlank() && validateDate(md) == null && md > objDeadlineTrimmed
        }

    val valid = title.trim().isNotEmpty() &&
        deadlineError == null &&
        !anyMsDeadlineError &&
        !anyMsTitleError &&
        !anyMsDeadlineAfterObjective

    Column(
        modifier = modifier
            .background(TeddTokens.Bg)
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {},
            ),
    ) {
        // ── Header ──
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.Bg)
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .padding(horizontal = 22.dp, vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = if (isNew) "NOUVEL OBJECTIF" else "MODIFIER",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.6.sp,
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = if (isNew) "Pose ton intention" else title.ifBlank { "Sans titre" },
                    color = TeddTokens.Ink,
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = (-0.2).sp,
                )
            }
            TeddIconButton(onClick = onClose, size = 30.dp) {
                Box(modifier = Modifier.rotate(45f)) {
                    TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
                }
            }
        }

        // ── Body ──
        Column(
            modifier = Modifier
                .weight(1f)
                .verticalScroll(rememberScrollState())
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp),
        ) {
            EditorTextField(
                label = "Titre",
                required = true,
                value = title,
                onValueChange = { title = it },
                placeholder = "Courir un marathon, lancer mon entreprise…",
                errorMessage = if (title.isNotEmpty() && title.isBlank()) "Le titre est obligatoire." else null,
            )
            EditorTextField(
                label = "Description",
                hint = "Précis et concret, en une ou deux phrases.",
                value = description,
                onValueChange = { description = it },
                placeholder = "Boucler 42,2 km à Lyon, sous 4h, sans blessure.",
                multiline = true,
            )
            EditorTextField(
                label = "Motivation",
                hint = "Pourquoi ? — sera rappelé quand tu douteras.",
                value = motivation,
                onValueChange = { motivation = it },
                placeholder = "Prouver que la régularité bat le talent.",
                multiline = true,
                italic = true,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                TeddDateField(
                    label = "Échéance",
                    value = deadline,
                    onValueChange = { deadline = it },
                    modifier = Modifier.weight(1f),
                )
                EditorStatusSelect(
                    value = status,
                    onValueChange = { status = it },
                    modifier = Modifier.weight(1f),
                )
            }
            EditorCategorySelect(
                categories = categories,
                selectedId = categoryId,
                onSelect = { categoryId = it },
            )
            if (skills.isNotEmpty()) {
                EditorSkillSelect(skills = skills, selectedId = skillId, onSelect = { skillId = if (skillId == it) null else it })
            }
            Column {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)) {
                    Text("Progression", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                    Text("Un jeton = 10 %", color = TeddTokens.InkMuted, fontSize = 11.sp)
                }
                TeddTokenProgress(value = progress, onChange = { progress = it })
            }

            // ── Décomposition (création) ──
            if (isNew) {
                MicroTasksEditor(drafts = microTasks, onChange = { microTasks = it })
            }

            // ── Étapes ──
            MilestonesEditor(
                drafts = milestoneDrafts,
                onChange = { milestoneDrafts = it },
                objectiveDeadline = objDeadlineTrimmed.takeIf { it.isNotBlank() && deadlineError == null },
            )
        }

        // ── Footer ──
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.Bg)
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            if (!isNew) {
                TeddButton(
                    text = "Supprimer",
                    onClick = onDelete,
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Md,
                    leadingIcon = {
                        TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted)
                    },
                )
            }
            Spacer(modifier = Modifier.weight(1f))
            TeddButton(
                text = "Annuler",
                onClick = onClose,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Md,
            )
            TeddButton(
                text = if (isNew) "Créer l'objectif" else "Enregistrer",
                onClick = {
                    onSave(
                        ObjectiveDraft(
                            title = title.trim(),
                            description = description.trim(),
                            motivation = motivation.trim(),
                            deadline = deadline.trim(),
                            // Spec §3.1 : année obligatoire — défaut année courante si pas dérivable du deadline.
                            year = deadline.trim().take(4).toLongOrNull() ?: initial?.year ?: OBJ_CURRENT_YEAR,
                            status = status,
                            progress = progress.coerceIn(0, 100),
                            categoryId = categoryId,
                            skillId = skillId,
                            milestones = milestoneDrafts.map { it.copy(title = it.title.trim(), deadline = it.deadline.trim()) },
                            microTasks = if (isNew) microTasks.map { it.copy(title = it.title.trim()) }.filter { it.title.isNotEmpty() } else emptyList(),
                        )
                    )
                },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Md,
                enabled = valid,
                leadingIcon = {
                    TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp)
                },
            )
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Milestones editor (inline list)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun MilestonesEditor(
    drafts: List<MilestoneDraft>,
    onChange: (List<MilestoneDraft>) -> Unit,
    /** Deadline de l'objectif au format ISO valide, ou null si non renseignée. */
    objectiveDeadline: String?,
) {
    Column {
        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = "ÉTAPES",
                color = TeddTokens.InkMuted,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = "(${drafts.size})",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                modifier = Modifier.padding(end = 8.dp),
            )
            TeddButton(
                text = "Ajouter",
                onClick = {
                    onChange(drafts + MilestoneDraft(id = null, title = "", deadline = ""))
                },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.Ink) },
            )
        }
        if (drafts.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(16.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = "Découpe ton objectif en étapes : une étape = une preuve mesurable, faite de micro-tâches.",
                    color = TeddTokens.InkMuted,
                    fontSize = 12.sp,
                )
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                drafts.forEachIndexed { idx, draft ->
                    MilestoneRowEditor(
                        index = idx,
                        draft = draft,
                        objectiveDeadline = objectiveDeadline,
                        onChange = { updated ->
                            onChange(drafts.mapIndexed { i, d -> if (i == idx) updated else d })
                        },
                        onRemove = {
                            onChange(drafts.filterIndexed { i, _ -> i != idx })
                        },
                    )
                }
            }
        }
    }
}



// ──────────────────────────────────────────────────────────────────────
// Field primitives
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun EditorTextField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    hint: String? = null,
    required: Boolean = false,
    multiline: Boolean = false,
    italic: Boolean = false,
    errorMessage: String? = null,
) {
    val hasError = errorMessage != null
    Column(modifier = modifier) {
        Row(
            verticalAlignment = Alignment.Bottom,
            modifier = Modifier.fillMaxWidth().padding(bottom = 6.dp),
        ) {
            Row(modifier = Modifier.weight(1f)) {
                Text(
                    text = label,
                    color = TeddTokens.InkMuted,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                if (required) {
                    Text(" *", color = TeddTokens.Danger, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                }
            }
            if (hint != null) {
                Text(text = hint, color = TeddTokens.InkMuted, fontSize = 11.sp)
            }
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightForField(multiline)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(
                    1.dp,
                    if (hasError) TeddTokens.Danger.copy(alpha = 0.5f) else TeddTokens.Line,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .padding(horizontal = 12.dp, vertical = if (multiline) 10.dp else 0.dp),
            contentAlignment = if (multiline) Alignment.TopStart else Alignment.CenterStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.fillMaxWidth(),
                singleLine = !multiline,
                textStyle = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 13.sp,
                    fontStyle = if (italic) FontStyle.Italic else FontStyle.Normal,
                ),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                decorationBox = { inner ->
                    if (value.isEmpty() && placeholder != null) {
                        Text(
                            text = placeholder,
                            color = TeddTokens.InkSubtle,
                            fontSize = 13.sp,
                            fontStyle = if (italic) FontStyle.Italic else FontStyle.Normal,
                        )
                    }
                    inner()
                },
            )
        }
        if (errorMessage != null) {
            Text(
                text = errorMessage,
                color = TeddTokens.Danger,
                fontSize = 11.sp,
                modifier = Modifier.padding(top = 4.dp, start = 2.dp),
            )
        }
    }
}

private fun Modifier.heightForField(multiline: Boolean): Modifier =
    if (multiline) this.then(Modifier.height(72.dp)) else this.then(Modifier.height(36.dp))

@Composable
private fun EditorStatusSelect(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val options = listOf(
        "draft" to "Brouillon",
        "active" to "Actif",
        "done" to "Terminé",
        "abandoned" to "Abandonné",
    )
    Column(modifier = modifier) {
        Text(
            text = "Statut",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(3.dp),
        ) {
            options.forEach { (key, label) ->
                val selected = value == key
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(30.dp)
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .then(
                            if (selected) Modifier.background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RXs))
                            else Modifier
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onValueChange(key) },
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = label,
                        color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
                        fontSize = 11.5.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
        }
    }
}

@Composable
private fun EditorCategorySelect(
    categories: List<Category>,
    selectedId: String?,
    onSelect: (String) -> Unit,
) {
    Column {
        Text(
            text = "Catégorie",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        androidx.compose.foundation.layout.FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            categories.forEach { c ->
                val color = runCatching { Color(parseHexColor(c.color)) }.getOrElse { TeddTokens.LimeStrong }
                val selected = c.id == selectedId
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(if (selected) color.copy(alpha = 0.10f) else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
                        .border(1.dp, if (selected) color else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onSelect(c.id) },
                        )
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    Box(modifier = Modifier.size(8.dp).clip(RoundedCornerShape(2.dp)).background(color))
                    Text(
                        text = c.name,
                        color = if (selected) color else TeddTokens.Ink,
                        fontSize = 12.5.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
        }
    }
}



// ──────────────────────────────────────────────────────────────────────
// helpers
// ──────────────────────────────────────────────────────────────────────
private fun parseHexColor(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}

private val DateRegex = Regex("^\\d{4}-\\d{2}-\\d{2}$")

/** Retourne un message d'erreur si la date n'est ni vide ni au format AAAA-MM-JJ. */
internal fun validateDate(value: String): String? {
    if (value.isBlank()) return null
    if (!DateRegex.matches(value.trim())) return "Format attendu : AAAA-MM-JJ"
    val parts = value.trim().split("-")
    val month = parts[1].toIntOrNull() ?: return "Mois invalide."
    val day = parts[2].toIntOrNull() ?: return "Jour invalide."
    if (month !in 1..12) return "Mois invalide (1-12)."
    if (day !in 1..31) return "Jour invalide (1-31)."
    return null
}

@Composable
private fun MilestoneRowEditor(
    index: Int,
    draft: MilestoneDraft,
    objectiveDeadline: String?,
    onChange: (MilestoneDraft) -> Unit,
    onRemove: () -> Unit,
) {
    val titleMissing = draft.title.isBlank()
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, if (titleMissing && draft.title.isNotEmpty()) TeddTokens.Danger.copy(alpha = 0.4f) else TeddTokens.Line, shape)
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Box(
                modifier = Modifier
                    .size(24.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs)),
                contentAlignment = Alignment.Center,
            ) {
                Text((index + 1).toString(), color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.Bold)
            }
            Box(modifier = Modifier.weight(1f).height(32.dp), contentAlignment = Alignment.CenterStart) {
                BasicTextField(
                    value = draft.title,
                    onValueChange = { onChange(draft.copy(title = it)) },
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp, fontWeight = FontWeight.Medium),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    modifier = Modifier.fillMaxWidth(),
                    decorationBox = { inner ->
                        if (draft.title.isEmpty()) {
                            Text("Ce que cette étape prouve…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                        }
                        inner()
                    },
                )
            }
            TeddIconButton(onClick = onRemove, size = 30.dp) {
                TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted)
            }
        }
        TeddDateField(
            label = null,
            value = draft.deadline,
            onValueChange = { onChange(draft.copy(deadline = it)) },
            placeholder = if (objectiveDeadline != null) "Échéance de l'étape (au plus tard le ${objectiveDeadline})" else "Échéance de l'étape",
            height = 36.dp,
            maxIso = objectiveDeadline,
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Décomposition initiale en micro-tâches (feuille de route v2, Duneier)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun MicroTasksEditor(drafts: List<MicroTaskDraft>, onChange: (List<MicroTaskDraft>) -> Unit) {
    Column {
        Row(modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp), verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f)) {
                Text("DÉCOMPOSITION PROPOSÉE", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, letterSpacing = 0.6.sp)
                Text("Le premier pas, jamais la montagne : des micro-tâches de 5 à 10 minutes.", color = TeddTokens.InkMuted, fontSize = 11.sp)
            }
            TeddButton(
                text = "Ajouter",
                onClick = { onChange(drafts + MicroTaskDraft("", 10)) },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.Ink) },
            )
        }
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            drafts.forEachIndexed { idx, d ->
                val shape = RoundedCornerShape(TeddTokens.RSm)
                Column(
                    modifier = Modifier.fillMaxWidth().clip(shape).background(TeddTokens.Surface, shape).padding(10.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Box(
                            modifier = Modifier.size(18.dp).clip(RoundedCornerShape(TeddTokens.RXs)).border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs)),
                        )
                        Box(modifier = Modifier.weight(1f).height(30.dp), contentAlignment = Alignment.CenterStart) {
                            BasicTextField(
                                value = d.title,
                                onValueChange = { v -> onChange(drafts.mapIndexed { i, x -> if (i == idx) x.copy(title = v) else x }) },
                                singleLine = true,
                                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp, fontWeight = FontWeight.Medium),
                                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                                modifier = Modifier.fillMaxWidth(),
                                decorationBox = { inner ->
                                    if (d.title.isEmpty()) Text("Un premier pas de 5 à 10 minutes…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                                    inner()
                                },
                            )
                        }
                        TeddIconButton(onClick = { onChange(drafts.filterIndexed { i, _ -> i != idx }) }, size = 28.dp) {
                            TeddIcon(name = "trash", size = 12.dp, color = TeddTokens.InkMuted)
                        }
                    }
                    TeddDurationChips(
                        value = d.minutes,
                        onChange = { m -> onChange(drafts.mapIndexed { i, x -> if (i == idx) x.copy(minutes = m) else x }) },
                        modifier = Modifier.padding(start = 26.dp),
                    )
                }
            }
        }
    }
}

/**
 * Rattachement à une compétence : le temps passé sur les micro-tâches de cet objectif
 * comptera dans les heures de la compétence, et donc dans ses paliers.
 */
@Composable
private fun EditorSkillSelect(skills: List<Skill>, selectedId: String?, onSelect: (String) -> Unit) {
    Column {
        Text(
            text = "Compétence",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.padding(bottom = 2.dp),
        )
        Text(
            text = "Le temps passé ici comptera dans ses heures.",
            color = TeddTokens.InkMuted,
            fontSize = 11.sp,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        androidx.compose.foundation.layout.FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            skills.forEach { sk ->
                val selected = sk.id == selectedId
                val color = runCatching { Color(parseHexColor(sk.color)) }.getOrElse { TeddTokens.LimeStrong }
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(if (selected) color.copy(alpha = 0.12f) else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onSelect(sk.id) },
                        )
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    TeddIcon(name = sk.icon, size = 12.dp, color = if (selected) color else TeddTokens.InkMuted, strokeWidth = 2.dp)
                    Text(
                        text = sk.name,
                        color = if (selected) color else TeddTokens.Ink,
                        fontSize = 12.5.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
        }
    }
}
